import { openDB } from 'idb'
import type { DBSchema, IDBPDatabase } from 'idb'
import type { FlowDefinition } from '@shared/schema/flow'
import type { StepAnswers } from '@shared/schema/answers'
import type { SimulatorSnapshot } from '@shared/engine/simulate'
import { countByStatus } from './outbox'

/**
 * ที่เก็บข้อมูลบนเครื่องพนักงาน
 *
 * แยกเป็น 4 ส่วนโดยตั้งใจ:
 *   defs   — definition ที่โหลดมาแล้ว เพื่อให้เปิดทำงานต่อได้แม้เน็ตหลุด
 *   runs   — สถานะงานที่ทำค้าง (กู้คืนเมื่อรีเฟรช/ปิดแอป)
 *   outbox — คิวรายการที่ต้องส่งขึ้นเซิร์ฟเวอร์ (ลบเมื่อเซิร์ฟเวอร์ตอบสำเร็จเท่านั้น)
 *   photos — ไฟล์รูป เก็บแยกจาก outbox เพื่อให้แถวในคิวเล็กและอ่านเร็ว
 */

export type OutboxOpKind = 'createSubmission' | 'step' | 'photo' | 'complete'
export type OutboxStatus = 'pending' | 'failed'

export type OutboxOp = {
  opId: string
  kind: OutboxOpKind
  submissionId: string
  payload: Record<string, unknown>
  attempt: number
  nextAttemptAt: number
  status: OutboxStatus
  error?: string
  createdAt: number
}

export type RunRecord = {
  submissionId: string
  workflowId: string
  versionId: string
  workflowName: string
  status: 'in_progress' | 'completed'
  snapshot: SimulatorSnapshot | null
  startedAt: number
  updatedAt: number
}

export type DefRecord = {
  versionId: string
  workflowId: string
  workflowName: string
  versionNumber: number
  definition: FlowDefinition
  cachedAt: number
}

export type PhotoRecord = {
  assetId: string
  blob: Blob
  contentType: string
  submissionId: string
  stepKey: string
  fieldName: string
  createdAt: number
}

interface RunnerDB extends DBSchema {
  defs: { key: string; value: DefRecord; indexes: { byWorkflow: string } }
  runs: { key: string; value: RunRecord; indexes: { byWorkflow: string; byStatus: string } }
  outbox: { key: string; value: OutboxOp; indexes: { bySubmission: string } }
  photos: { key: string; value: PhotoRecord; indexes: { bySubmission: string } }
}

const DB_NAME = 'ayi-runner'
const DB_VERSION = 1

let dbPromise: Promise<IDBPDatabase<RunnerDB>> | null = null

export function getDb(): Promise<IDBPDatabase<RunnerDB>> {
  if (!dbPromise) {
    dbPromise = openDB<RunnerDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const defs = db.createObjectStore('defs', { keyPath: 'versionId' })
        defs.createIndex('byWorkflow', 'workflowId')

        const runs = db.createObjectStore('runs', { keyPath: 'submissionId' })
        runs.createIndex('byWorkflow', 'workflowId')
        runs.createIndex('byStatus', 'status')

        const outbox = db.createObjectStore('outbox', { keyPath: 'opId' })
        outbox.createIndex('bySubmission', 'submissionId')

        const photos = db.createObjectStore('photos', { keyPath: 'assetId' })
        photos.createIndex('bySubmission', 'submissionId')
      },
    })
  }
  return dbPromise
}

/**
 * ขอให้เบราว์เซอร์อย่าล้างข้อมูลของเราตอนพื้นที่เต็ม
 * สำคัญมากกับงานภาคสนาม: คำตอบที่ยังส่งไม่ขึ้นต้องไม่หายไปเอง
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false
  try {
    if (await navigator.storage.persisted()) return true
    return await navigator.storage.persist()
  } catch {
    return false
  }
}

export async function estimateStorage(): Promise<{ usage: number; quota: number } | null> {
  if (!navigator.storage?.estimate) return null
  try {
    const estimate = await navigator.storage.estimate()
    return { usage: estimate.usage ?? 0, quota: estimate.quota ?? 0 }
  } catch {
    return null
  }
}

// ---------------------------------------------------------------- defs

export async function cacheDefinition(record: DefRecord): Promise<void> {
  const db = await getDb()
  await db.put('defs', record)
}

export async function getCachedDefinitionByWorkflow(workflowId: string): Promise<DefRecord | null> {
  const db = await getDb()
  const records = await db.getAllFromIndex('defs', 'byWorkflow', workflowId)
  if (records.length === 0) return null
  return records.reduce((latest, item) => (item.versionNumber > latest.versionNumber ? item : latest))
}

export async function getCachedDefinition(versionId: string): Promise<DefRecord | null> {
  const db = await getDb()
  return (await db.get('defs', versionId)) ?? null
}

// ---------------------------------------------------------------- runs

export async function saveRun(record: RunRecord): Promise<void> {
  const db = await getDb()
  await db.put('runs', record)
}

export async function getRun(submissionId: string): Promise<RunRecord | null> {
  const db = await getDb()
  return (await db.get('runs', submissionId)) ?? null
}

/** งานที่ยังทำค้างของแบบสอบถามนี้ (ใช้ถามผู้ใช้ว่าจะทำต่อไหม) */
export async function findResumableRun(workflowId: string): Promise<RunRecord | null> {
  const db = await getDb()
  const records = await db.getAllFromIndex('runs', 'byWorkflow', workflowId)
  const candidates = records.filter((item) => item.status === 'in_progress' && item.snapshot !== null)
  if (candidates.length === 0) return null
  return candidates.reduce((latest, item) => (item.updatedAt > latest.updatedAt ? item : latest))
}

export async function listRuns(): Promise<RunRecord[]> {
  const db = await getDb()
  const records = await db.getAll('runs')
  return records.sort((a, b) => b.updatedAt - a.updatedAt)
}

export async function deleteRun(submissionId: string): Promise<void> {
  const db = await getDb()
  await db.delete('runs', submissionId)
}

// ---------------------------------------------------------------- outbox

export async function putOp(op: OutboxOp): Promise<void> {
  const db = await getDb()
  await db.put('outbox', op)
}

export async function listOps(): Promise<OutboxOp[]> {
  const db = await getDb()
  const ops = await db.getAll('outbox')
  return ops.sort((a, b) => (a.opId < b.opId ? -1 : a.opId > b.opId ? 1 : 0))
}

export async function deleteOp(opId: string): Promise<void> {
  const db = await getDb()
  await db.delete('outbox', opId)
}

/** แยกนับ pending/failed — ดูเหตุผลที่ countByStatus() ใน outbox.ts */
export async function countOutbox(): Promise<{ pending: number; failed: number }> {
  const db = await getDb()
  return countByStatus(await db.getAll('outbox'))
}

/** ให้โอกาสส่งใหม่อีกครั้งสำหรับรายการที่เคยล้มเหลวถาวร (เช่น หลังผู้ดูแลแก้ปัญหาที่ต้นเหตุแล้ว) */
export async function resetFailedOps(): Promise<number> {
  const db = await getDb()
  const all = await db.getAll('outbox')
  const failed = all.filter((op) => op.status === 'failed')
  for (const op of failed) {
    await db.put('outbox', { ...op, status: 'pending', attempt: 0, nextAttemptAt: Date.now() })
  }
  return failed.length
}

/**
 * ล้างข้อมูลในเครื่องของงานที่ถูกลบไปแล้วที่ต้นทาง (เช่นแอดมินลบทิ้งจากหลังบ้าน)
 * ไม่งั้นเครื่องพนักงานจะเห็นเป็น "ทำค้างไว้"/"ส่งไม่สำเร็จถาวร" ค้างตลอดไปทั้งที่ข้อมูลนั้นไม่มีอยู่จริงแล้ว
 */
export async function purgeSubmission(submissionId: string): Promise<{ purgedAssetIds: string[] }> {
  const db = await getDb()
  const [ops, photos] = await Promise.all([
    db.getAllFromIndex('outbox', 'bySubmission', submissionId),
    db.getAllFromIndex('photos', 'bySubmission', submissionId),
  ])
  await Promise.all([
    ...ops.map((op) => db.delete('outbox', op.opId)),
    ...photos.map((photo) => db.delete('photos', photo.assetId)),
    db.delete('runs', submissionId),
  ])
  return { purgedAssetIds: photos.map((photo) => photo.assetId) }
}

// ---------------------------------------------------------------- photos

export async function putPhoto(record: PhotoRecord): Promise<void> {
  const db = await getDb()
  await db.put('photos', record)
}

export async function getPhoto(assetId: string): Promise<PhotoRecord | null> {
  const db = await getDb()
  return (await db.get('photos', assetId)) ?? null
}

export async function deletePhoto(assetId: string): Promise<void> {
  const db = await getDb()
  await db.delete('photos', assetId)
}

export type { StepAnswers }
